import React, { useEffect, useRef, useState } from 'react';

const BOARD_COLORS = ['#0079bf', '#d29034', '#519839', '#b04632', '#89609e', '#cd5a91', '#4bbf6b', '#00aecc'];

export interface InlineCreationFormProps {
  readonly placeholder: string;
  readonly submitLabel: string;
  readonly onSubmit: (value: string, color?: string) => Promise<void>;
  readonly onCancel: () => void;
  readonly error?: string | null;
  readonly showColorPicker?: boolean;
  readonly onColorSelect?: (color: string) => void;
  readonly selectedColor?: string;
}

/**
 * Reusable inline form for creating cards, lists, and boards.
 * Auto-focuses on mount, Enter submits, Escape cancels.
 * Keeps the form open after successful submit for rapid entry.
 */
export function InlineCreationForm({
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  error,
  showColorPicker = false,
  onColorSelect,
  selectedColor,
}: InlineCreationFormProps): React.ReactElement {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(): Promise<void> {
    const trimmed = value.trim();
    if (trimmed === '' || loading) return;

    setLoading(true);
    try {
      await onSubmit(trimmed, selectedColor);
      setValue('');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSubmit();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
    }
  }

  return (
    <div className="flex flex-col gap-2" role="form" aria-label="Inline creation form">
      <label htmlFor="inline-creation-input" className="sr-only">
        {placeholder}
      </label>
      <input
        id="inline-creation-input"
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={loading}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? 'inline-creation-error' : undefined}
        className="rounded-md border border-input bg-card px-3 py-2 text-sm text-card-foreground ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
      />

      {error && (
        <p id="inline-creation-error" className="text-sm text-destructive mt-1" role="alert">
          {error}
        </p>
      )}

      {showColorPicker && (
        <ColorPicker
          selectedColor={selectedColor}
          onColorSelect={onColorSelect}
        />
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={loading || value.trim() === ''}
          aria-busy={loading}
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving…' : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

interface ColorPickerProps {
  readonly selectedColor?: string;
  readonly onColorSelect?: (color: string) => void;
}

function ColorPicker({ selectedColor, onColorSelect }: ColorPickerProps): React.ReactElement {
  return (
    <fieldset className="flex flex-wrap gap-2" aria-label="Board color selection">
      <legend className="sr-only">Select a board color</legend>
      {BOARD_COLORS.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onColorSelect?.(color)}
          aria-label={`Select color ${color}`}
          aria-pressed={selectedColor === color}
          className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{
            backgroundColor: color,
            borderColor: selectedColor === color ? 'hsl(var(--foreground))' : 'transparent',
          }}
        />
      ))}
    </fieldset>
  );
}
